import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api, { auth } from '../lib/api';

const AuthContext = createContext(null);

// Inactivity timeout: 30 minutes in milliseconds
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const inactivityTimerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const sessionVersionRef = useRef(0);

  // Reset the inactivity timer
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    
    // Only set timer if user is logged in
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      inactivityTimerRef.current = setTimeout(() => {
        console.log('Session expired due to inactivity');
        logoutDueToInactivity();
      }, INACTIVITY_TIMEOUT);
    }
  }, []);

  // Logout due to inactivity (with alert)
  const logoutDueToInactivity = async () => {
    try { await auth.logout(); } catch {
      alert('Logout could not reach the server. Please retry logout.');
      return;
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    alert('Your session has expired due to 30 minutes of inactivity. Please login again.');
    window.location.href = '/login';
  };

  // Setup activity listeners
  useEffect(() => {
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      resetInactivityTimer();
    };

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  // Check for existing session on load
  useEffect(() => {
    const version = sessionVersionRef.current;
    let active = true;
    { // Restore through the HttpOnly cookie, or exchange a legacy session first.
      auth.me()
        .then(res => {
          if (!active || version !== sessionVersionRef.current) return;
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
          resetInactivityTimer();
        })
        .catch(() => {
          if (!active || version !== sessionVersionRef.current) return;
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => { if (active) setLoading(false); });
    }
    return () => { active = false; };
  }, [resetInactivityTimer]);

  const login = async (credentials) => {
    sessionVersionRef.current += 1;
    const response = await auth.login(credentials);
    const { user } = response.data;
    localStorage.removeItem('token');
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    resetInactivityTimer();
    return user;
  };

  const register = async (data) => {
    sessionVersionRef.current += 1;
    const response = await auth.register(data);
    const { user } = response.data;
    localStorage.removeItem('token');
    localStorage.setItem('user', JSON.stringify(user));
    setUser(user);
    resetInactivityTimer();
    return user;
  };

  const loginSuperAdmin = async (credentials) => {
    sessionVersionRef.current += 1;
    const response = await api.post('/super-admin/login', credentials);
    localStorage.removeItem('token');
    localStorage.setItem('user', JSON.stringify(response.data.user));
    setUser(response.data.user);
    resetInactivityTimer();
    return response.data.user;
  };

  const updateUser = useCallback((updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, []);

  const logout = async () => {
    await auth.logout();
    sessionVersionRef.current += 1;
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ user, login, loginSuperAdmin, register, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};
